// Language-independent assets: icons, images, numbers. Text lives in strings.js
import {
  Boxes, Truck, Wrench, HardHat, Package, Factory, Store, HeartPulse, Warehouse,
  ScanLine, History, BellRing, RefreshCw, Database, TrendingUp, ShieldCheck,
  Smartphone, Wifi, Globe, Clock, Lock, Cpu, BatteryCharging, Radio, Wallet, Gauge,
  Waves, ShieldAlert, Zap, KeyRound, Thermometer, BadgeCheck, Building2,
} from 'lucide-react';

export const NAV_HREFS = ['#solution', '#fonctionnalites', '#tag', '#roi', '#tarifs', '#faq'];

export const ICONS = {
  sectors: [Boxes, Factory, Wrench, Truck, Package],
  connectivity: [Smartphone, Wifi, Clock, Globe, Lock],
  industries: [Store, Factory, HeartPulse, HardHat, Warehouse],
  features: [ScanLine, History, BellRing, Globe, Gauge, Database, TrendingUp, RefreshCw],
  tagSpecs: [Cpu, BatteryCharging, Waves, ShieldAlert, Smartphone, Radio],
  advantages: [Smartphone, Wallet, ShieldCheck, Gauge],
  range: [KeyRound, Waves, Radio, Boxes, Thermometer, BadgeCheck],
  useCases: [HardHat, Warehouse, HeartPulse, Factory, Truck, Building2],
  liveAssets: [Truck, Boxes, Wrench, Zap, Package],
};

export const IMAGES = {
  industries: [
    'https://images.unsplash.com/photo-1587293852726-70cdb56c2866?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA0MTJ8MHwxfHNlYXJjaHwxfHx3YXJlaG91c2UlMjBsb2dpc3RpY3N8ZW58MHx8fHwxNzg3ODQ5Njc5fDA&ixlib=rb-4.1.0&q=85',
    'https://images.pexels.com/photos/27084598/pexels-photo-27084598.jpeg',
    'https://images.unsplash.com/photo-1526628953301-3e589a6a8b74',
    'https://images.pexels.com/photos/1009926/pexels-photo-1009926.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
    'https://images.unsplash.com/photo-1553413077-190dd305871c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA0MTJ8MHwxfHNlYXJjaHwzfHx3YXJlaG91c2UlMjBsb2dpc3RpY3N8ZW58MHx8fHwxNzg3ODQ5Njc5fDA&ixlib=rb-4.1.0&q=85',
  ],
  solutions: [
    'https://images.unsplash.com/photo-1551288049-bebda4e38f71',
    'https://images.pexels.com/photos/31112238/pexels-photo-31112238.jpeg',
    'https://images.pexels.com/photos/4487383/pexels-photo-4487383.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
    'https://images.unsplash.com/photo-1494412519320-aa613dfb7738',
  ],
  tag: '/tag-k12-c.png',
};

export const NUM = {
  stats: [{ value: 25, suffix: '%' }, { value: 60, suffix: '%' }],
  pricing: [
    { price: '8', highlighted: false },
    { price: '12', highlighted: true },
    { price: null, highlighted: false },
  ],
  // ROI calculator defaults & bounds
  roi: {
    assets: { min: 5, max: 500, step: 5, default: 60 },
    avgValue: { min: 500, max: 20000, step: 100, default: 3500 },
    hours: { min: 1, max: 40, step: 1, default: 8 },
    hourlyCost: 45,        // CHF per hour
    annualLossRate: 0.06,  // 6% of fleet value lost/stolen/damaged per year
    lossReduction: 0.6,    // LOGITAG cuts losses by 60%
    utilRate: 0.05,        // 5% of fleet value wasted on under-use / duplicate buys
    utilReduction: 0.5,    // LOGITAG recovers half of it
    timeReduction: 0.25,   // 25% time saved
    tagMonthly: 12,        // Business plan CHF / tag / month
  },
};

// Language-neutral technical specs for each tag format (aligned with ICONS.range order)
export const RANGE_SPECS = [
  { dims: '58 × 32 × 10 mm', weight: '11 g', range: '80 m', ip: 'IP54' },
  { dims: '54 × 46 × 20 mm', weight: '30 g', range: '120 m', ip: 'IP67' },
  { dims: '62 × 40 × 19 mm', weight: '28 g', range: '300 m', ip: 'IP67' },
  { dims: '90 × 40 × 15 mm', weight: '45 g', range: '150 m', ip: 'IP68 · IK08' },
  { dims: '50 × 50 × 20 mm', weight: '35 g', range: '120 m', ip: 'IP67' },
  { dims: '40 × 34 × 9 mm', weight: '9 g', range: '70 m', ip: 'IP65' },
];

export const MODELS = [
  { name: 'K12', ip: 'IP67', years: 3, icon: Waves, img: '/tag-k12-c.png', highlight: true },
  { name: 'K3', ip: 'IP68', years: 4, icon: Cpu, img: '/tag-k3-c.png' },
  { name: 'K4', ip: 'IP67', years: 4, icon: Boxes, img: '/tag-k4-c.png' },
  { name: 'K9', ip: 'IP67', years: 8, icon: Package, img: '/tag-k9-c.png' },
  { name: 'K6P', ip: 'IP67', years: 8, icon: Thermometer, img: '/tag-k6p-c.png' },
  { name: 'K5P', ip: 'IP67', years: 5, icon: Radio, img: '/tag-k5p-c.png' },
];

export const BROCHURE_URL = 'https://www.logitag.ch/_files/ugd/a87703_ece30eab886d47e39ce581b062976ea9.pdf';
export const VIDEO_EMBED = 'https://www.youtube.com/embed/R9M89wbfzM8?autoplay=1&rel=0';
export const CONTACT_STATIC = { email: 'info@logitrak.ch', phone: '+41 (0)21 635 44 22' };

// Field gallery (Swiss job sites / logistics)
export const GALLERY = [
  'https://images.unsplash.com/photo-1659427921734-d4590f1e099f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1NzZ8MHwxfHNlYXJjaHwyfHxzd2lzcyUyMGNvbnN0cnVjdGlvbnxlbnwwfHx8fDE3ODc5MjMwNTB8MA&ixlib=rb-4.1.0&q=85',
  'https://images.unsplash.com/photo-1589939705384-5185137a7f0f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzl8MHwxfHNlYXJjaHwzfHxjb25zdHJ1Y3Rpb24lMjB3b3JrZXJzfGVufDB8fHx8MTc4NzkyMzA1MHww&ixlib=rb-4.1.0&q=85',
  'https://images.pexels.com/photos/12314551/pexels-photo-12314551.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
  'https://images.unsplash.com/photo-1547895749-888a559fc2a7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MTJ8MHwxfHNlYXJjaHw0fHxpbmR1c3RyaWFsJTIwd2FyZWhvdXNlfGVufDB8fHx8MTc4NzkyMzA1MHww&ixlib=rb-4.1.0&q=85',
  'https://images.pexels.com/photos/27084598/pexels-photo-27084598.jpeg',
  'https://images.pexels.com/photos/4487383/pexels-photo-4487383.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
];

// Localized hero banner (baked-in text). Generated variants served from /public.
export const HERO_IMAGES = {
  fr: '/hero-fr.png',
  en: '/hero-en.png',
  de: '/hero-de.png',
};
